import { checkNullString } from '@/pages/datalab/utils/utils';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { TDispatchFun } from '@/pages/datalab/components/SortableDrag/configration';
import { normalizeDimensionField } from '@/pages/datalab/components/SortableDrag/utils/dimensionSortablePersist';

/**
 * 表格原始列区写回 model 的统一入口。
 * 覆盖拖拽落库、close 删除、批量下拉增删。
 */

export interface IRawColumnSortablePersistOptions {
  sortableKey: string;
  chartType: string;
  dispatchFun: TDispatchFun;
  /** save 时透传 saveColumnInfos，与 model 快照一致 */
  saveColumnInfos?: Record<string, unknown>;
}

export interface IRawColumnSortablePersist {
  saveList: (list: IColumnInfo[]) => void;
  removeItems: (currentList: IColumnInfo[], removedItems: IColumnInfo[]) => void;
  applyBatchChange: (
    nextList: IColumnInfo[],
    removedItems: IColumnInfo[],
  ) => void;
}

export const createRawColumnSortablePersist = (
  options: IRawColumnSortablePersistOptions,
): IRawColumnSortablePersist => {
  const { sortableKey, chartType, dispatchFun, saveColumnInfos } = options;

  const normalizeRawColumnList = (list: IColumnInfo[]): IColumnInfo[] =>
    list
      .filter(item => !checkNullString(item.id))
      .map(item => normalizeDimensionField(item, chartType));

  const buildSavePayload = (nextList: IColumnInfo[]): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      [sortableKey]: nextList,
    };
    if (saveColumnInfos) {
      payload.saveColumnInfos = saveColumnInfos;
    }
    return payload;
  };

  const saveList = (list: IColumnInfo[]): void => {
    dispatchFun('save', buildSavePayload(normalizeRawColumnList(list)));
  };

  const applyBatchChange = (
    nextList: IColumnInfo[],
    _removedItems: IColumnInfo[],
  ): void => {
    dispatchFun('save', buildSavePayload(normalizeRawColumnList(nextList)));
  };

  const removeItems = (
    currentList: IColumnInfo[],
    removedItems: IColumnInfo[],
  ): void => {
    if (!removedItems.length) {
      return;
    }
    const removedKeySet = new Set(
      removedItems.map(item => `${item.id}_${item.table_code}`),
    );
    const nextList = currentList.filter(item => {
      if (checkNullString(item.id)) {
        return false;
      }
      return !removedKeySet.has(`${item.id}_${item.table_code}`);
    });
    applyBatchChange(nextList, removedItems);
  };

  return {
    saveList,
    removeItems,
    applyBatchChange,
  };
};
